import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  const navBgClass = isScrolled || !isHome
    ? 'bg-secondary-900/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-fire-line text-accent-500 text-2xl"></i>
            </div>
            <span className={`font-heading text-lg font-bold ${isScrolled || !isHome ? 'text-foreground-50' : 'text-background-50'}`}>
              51fun吃瓜
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname === '/'
                  ? (isScrolled || !isHome ? 'text-accent-500' : 'text-accent-400')
                  : (isScrolled || !isHome ? 'text-foreground-200 hover:text-accent-500' : 'text-background-50/80 hover:text-accent-300')
              }`}
            >
              首页
            </Link>

            {/* Category Dropdown */}
            <div className="relative" onMouseEnter={() => setIsCategoryOpen(true)} onMouseLeave={() => setIsCategoryOpen(false)}>
              <button
                className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1 ${
                  location.pathname.includes('/category/')
                    ? (isScrolled || !isHome ? 'text-accent-500' : 'text-accent-400')
                    : (isScrolled || !isHome ? 'text-foreground-200 hover:text-accent-500' : 'text-background-50/80 hover:text-accent-300')
                }`}
              >
                分类吃瓜
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </div>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-secondary-900/95 backdrop-blur-md rounded-lg py-2 border border-foreground-700/30">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-200 hover:text-accent-500 hover:bg-foreground-800/30 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname === '/about'
                  ? (isScrolled || !isHome ? 'text-accent-500' : 'text-accent-400')
                  : (isScrolled || !isHome ? 'text-foreground-200 hover:text-accent-500' : 'text-background-50/80 hover:text-accent-300')
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Search + Subscribe Button */}
          <div className="hidden md:flex items-center gap-4">
            <button className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              isScrolled || !isHome ? 'text-foreground-200 hover:text-accent-500' : 'text-background-50/80 hover:text-accent-300'
            }`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-lg"></i>
              </div>
            </button>
            <button className="whitespace-nowrap cursor-pointer px-5 py-2 rounded-full border border-accent-500 text-accent-500 text-sm font-medium hover:bg-accent-500 hover:text-background-50 transition-all">
              订阅爆料
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
              isScrolled || !isHome ? 'text-foreground-50' : 'text-background-50'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-secondary-900/98 backdrop-blur-md border-t border-foreground-700/30">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer ${
                location.pathname === '/' ? 'text-accent-500 bg-foreground-800/30' : 'text-foreground-200 hover:bg-foreground-800/20'
              }`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer ${
                  location.pathname === `/category/${cat.slug}` ? 'text-accent-500 bg-foreground-800/30' : 'text-foreground-200 hover:bg-foreground-800/20'
                }`}
              >
                <div className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer ${
                location.pathname === '/about' ? 'text-accent-500 bg-foreground-800/30' : 'text-foreground-200 hover:bg-foreground-800/20'
              }`}
            >
              关于我们
            </Link>
            <button className="w-full mt-2 whitespace-nowrap cursor-pointer px-5 py-3 rounded-full border border-accent-500 text-accent-500 text-sm font-medium hover:bg-accent-500 hover:text-background-50 transition-all">
              订阅爆料
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}